/**
 * ViettelCA USB Token Provider
 * Implements IUsbTokenProvider interface for ViettelCA digital signature service
 *
 * Supports two signing methods:
 * 1. ViettelCA Plugin (Port 14007) - For file signing via native plugin
 * 2. Java Sign Server (Port 14500) - For BHYT XML signing via Windows Certificate Store
 */

import axios from "axios";
import { createXHR, getInput } from "./helpers";
import { IUsbTokenProvider } from "./usbProviderInterface";

// ========================================
// Internal State
// ========================================
let hSession: string = "";
let processing = false;
let domain = "http://127.0.0.1:14007/"; // ViettelCA Plugin default port
let inputPath = "";
let outputPath = "";

const LibList_MACOS = "viettel-ca_v6.dylib;viettel-ca_v5.dylib;viettel-ca_v4.dylib";
const LibList_WIN = "viettel-ca_v6.dll;viettel-ca_v5.dll;viettel-ca_v4.dll;viettel-ca_v2.dll";

// ========================================
// Error Handling
// ========================================
function showErrMsg_CMS(code: string): void {
  switch (code) {
    case '100100':
      alert('Lỗi: Không có chứng thư số.');
      break;
    case '100101':
      alert('Lỗi: Tương tác SignPlugin.');
      break;
    case '100102':
    case '100202':
      alert('Lỗi: Chứng thư số không hợp lệ hoặc không có quyền sử dụng. Vui lòng F5 lại trình duyệt và thử lại');
      break;
    case '100103':
    case '100205':
    case '100303':
      alert('Lỗi: Session không hợp lệ');
      break;
    case '100104':
      alert('Lỗi: Chứng thư số hết hạn');
      break;
    case '100200':
      alert('Lỗi: Dữ liệu không hợp lệ');
      break;
    case '100201':
      alert('Lỗi: Không tìm thấy chứng thư số');
      break;
    case '100203':
      alert('Lỗi: Lỗi trong quá trình ký');
      break;
    case '100204':
      alert('Lỗi: Lỗi bộ nhớ không đủ');
      break;
    case '100300':
      alert('Lỗi: Chữ ký không đúng định dạng');
      break;
    case '100301':
      alert('Lỗi: Phân tích chứng thư số');
      break;
    case '100302':
      alert('Lỗi: Chữ ký không hợp lệ');
      break;
    default:
      alert('Lỗi: ' + code);
      break;
  }
}

function showErrMsg_Docs(code: string): void {
  switch (code) {
    case '100101':
    case '100201':
    case '100301':
    case '101002':
    case '101102':
      alert('Lỗi: Chép file.');
      break;
    case '100102':
    case '100202':
    case '100302':
      alert('Lỗi: Tài liệu bị mã hóa, không thể ký');
      break;
    case '100103':
    case '100203':
    case '100303':
    case '100701':
    case '100802':
      alert('Lỗi: Không tìm thấy chứng thư số để ký');
      break;
    case '100104':
    case '100204':
    case '100304':
    case '100803':
      alert('Lỗi: Chứng thư số bị lỗi, không thể ký');
      break;
    case '100105':
    case '100205':
    case '100305':
    case '100804':
      alert('Lỗi: Chứng thư số không hợp lệ hoặc không có quyền sử dụng. Vui lòng F5 lại trình duyệt và thử lại');
      break;
    case '100106':
    case '100206':
    case '100306':
    case '101003':
    case '101103':
      alert('Lỗi: Đọc file');
      break;
    case '100107':
    case '100207':
    case '100307':
    case '100805':
      alert('Lỗi: Lỗi trong quá trình ký');
      break;
    case '100400':
    case '100401':
      alert('Lỗi: Định dạng file không hỗ trợ');
      break;
    case '100402':
      alert('Lỗi: Tên file quá dài');
      break;
    case '100403':
      alert('Lỗi: Kích thước file quá lớn');
      break;
    case '100404':
      alert('Lỗi: Chưa thiết lập link upload');
      break;
    case '100405':
    case '100406':
    case '100407':
    case '100408':
    case '101004':
    case '101104':
      alert('Lỗi: Lỗi trong quá trình upload file');
      break;
    case '100600':
      alert('Lỗi: Hủy chọn file');
      break;
    case '100601':
      alert('Lỗi: Hủy lưu file');
      break;
    case '100500':
    case '101001':
    case '101101':
    case '101301':
      alert('Lỗi: Input file');
      break;
    case '100501':
      alert('Lỗi: Output file');
      break;
    case '100502':
    case '100700':
    case '100800':
    case '101000':
    case '101100':
    case '101200':
    case '101300':
      alert('Lỗi: Session không hợp lệ');
      break;
    case '100801':
      alert('Lỗi: Dữ liệu XML không đúng định dạng');
      break;
    case '101201':
      alert('Lỗi: Định dạng thời gian không đúng');
      break;
    default:
      alert('Lỗi: ' + code);
      break;
  }
}

// ========================================
// ViettelCA Plugin Functions (Port 14007)
// ========================================
function initPlugin(): void {
  //=================>>Check OS<<=================
  let OSName = "Unknown";
  const ua = window.navigator.userAgent;
  if (ua.indexOf("Windows NT 6.2") !== -1) OSName = "Windows 8";
  if (ua.indexOf("Windows NT 6.1") !== -1) OSName = "Windows 7";
  if (ua.indexOf("Windows NT 6.0") !== -1) OSName = "Windows Vista";
  if (ua.indexOf("Windows NT 5.1") !== -1) OSName = "Windows XP";
  if (ua.indexOf("Windows NT 5.0") !== -1) OSName = "Windows 2000";
  if (ua.indexOf("Mac") !== -1) OSName = "Mac/iOS";
  if (ua.indexOf("X11") !== -1) OSName = "UNIX";
  if (ua.indexOf("Linux") !== -1) OSName = "Linux";
  //=================>>Check OS<<=================

  const xmlhttp = createXHR();
  let response = "";

  xmlhttp.onreadystatechange = function () {
    if (xmlhttp.readyState === 4 && xmlhttp.status === 200) {
      response = xmlhttp.responseText;
      handleResponse(response);
    }
  };

  xmlhttp.open("POST", domain + "getSession", true);
  if (OSName === "Mac/iOS") {
    xmlhttp.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
    xmlhttp.send("liblist=" + LibList_MACOS);
  } else if ((OSName === "UNIX") || (OSName === "Linux")) {
    alert("Not Support");
  } else {
    xmlhttp.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
    xmlhttp.send("liblist=" + LibList_WIN);
  }
}

function handleResponse(response: string): void {
  processing = false;
  if (response) {
    hSession = response;
    console.log("ViettelCA Plugin session connected: ", response);
    return;
  }
  alert("Vui lòng cài đặt SignPlugin hoặc cắm Token vào máy và bấm F5 để thử lại");
  window.open("./signplugin_viettel-ca_installer.exe");
}

async function getCertificate(): Promise<void> {
  if (processing) return;
  processing = true;
  try {
    const res = await axios.post(domain + "getCertificate", `sessionID=${hSession}`, {
      headers: {"Content-Type": "application/x-www-form-urlencoded"}
    });
    const certRawInput = getInput('cert_rawData');
    if (certRawInput) certRawInput.value = res.data;
  } catch {
    showErrMsg_CMS("getCertificate failed");
  } finally {
    processing = false;
  }
}

async function signData(): Promise<void> {
  if (processing) return;
  const signDataEl = getInput('sign_Data');
  const signSigEl = getInput('sign_Signature');
  const text = signDataEl?.value ?? "";
  if (!text) {
    alert("Vui long nhap van ban de ky");
    return;
  }
  processing = true;
  try {
    const res = await axios.post(domain + "Sign", `sessionID=${hSession}&inData=${text}`, {
      headers: {"Content-Type": "application/x-www-form-urlencoded"}
    });
    if (signSigEl) signSigEl.value = res.data;
  } catch {
    showErrMsg_CMS("signData failed");
  } finally {
    processing = false;
  }
}

export const browseFile = (): void => {
  const reqOpen = createXHR();
  reqOpen.onreadystatechange = function () {
    if (reqOpen.readyState === 4 && reqOpen.status === 200) {
      const fileInput = getInput('file_input');
      if (fileInput) fileInput.value = reqOpen.responseText;
      inputPath = reqOpen.responseText;

      if (inputPath === "") {
        const fileSigned = getInput('file_signed');
        if (fileSigned) fileSigned.value = "";
        outputPath = "";
        processing = false;
      } else {
        const reqSave = createXHR();
        reqSave.onreadystatechange = function () {
          if (reqSave.readyState === 4 && reqSave.status === 200) {
            const fileSigned = getInput('file_signed');
            if (fileSigned) fileSigned.value = reqSave.responseText;
            outputPath = reqSave.responseText;
            processing = false;
          }
        };
        reqSave.open("POST", domain + "SaveFile", true);
        reqSave.send();
      }
    } else {
      processing = true;
    }
  };
  reqOpen.open("POST", domain + "OpenFile", true);
  reqOpen.send();
}

async function internalSignFile(): Promise<void> {
  if (processing) return;
  const inputFile = inputPath;
  const outputFile = outputPath;

  return new Promise((resolve, reject) => {
    const reqSignFile = createXHR();
    reqSignFile.onreadystatechange = function () {
      if (reqSignFile.readyState === 4 && reqSignFile.status === 200) {
        const ret = reqSignFile.responseText;
        if (ret !== "") {
          processing = false;
          alert("Ky file thanh cong: " + ret);
          resolve();
        } else {
          const ReqLastErr = createXHR();
          ReqLastErr.onreadystatechange = function () {
            if (ReqLastErr.readyState === 4 && ReqLastErr.status === 200) {
              processing = false;
              showErrMsg_Docs(ReqLastErr.responseText);
              reject(new Error(ReqLastErr.responseText));
            }
          };
          ReqLastErr.open("POST", domain + "getLastErr", true);
          ReqLastErr.send();
        }
      } else {
        processing = true;
      }
    };

    reqSignFile.open("POST", domain + "fileSign", true);
    reqSignFile.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
    reqSignFile.send(
      "sessionID=" + hSession
      + "&inputFile=" + encodeURIComponent(inputFile)
      + "&outputFile=" + encodeURIComponent(outputFile)
      + "&xmlSignType=0&tagXMLData=data"
    );
  });
}

async function saveFile(): Promise<void> {
  if (processing) return;
  processing = true;
  try {
    const res = await axios.post(domain + "SaveFile");
    const fileSigned = getInput('file_signed');
    if (fileSigned) fileSigned.value = res.data;
  } catch {
    showErrMsg_Docs("saveFile failed");
  } finally {
    processing = false;
  }
}

/**
 * Sign XML using ViettelCA Plugin (Port 14007)
 */
async function signXMLDataViaPlugin(xmlContent: string): Promise<string> {
  if (processing) {
    throw new Error('Đang xử lý, vui lòng chờ');
  }

  if (!xmlContent) {
    throw new Error('Vui lòng nhập văn bản để ký');
  }

  processing = true;
  try {
    const res = await axios.post(domain + "signXMLData",
      `sessionID=${hSession}&xmlData=${xmlContent}&signType=0&tagXMLData=*`,
      {headers: {"Content-Type": "application/x-www-form-urlencoded"}}
    );
    return res.data;
  } catch (error) {
    showErrMsg_Docs("signXMLData failed");
    throw error;
  } finally {
    processing = false;
  }
}

// ========================================
// Java Sign Server Functions (Port 14500)
// For BHYT XML signing via Windows Certificate Store
// ========================================
async function signBHYTXml(xmlContent: string): Promise<string> {
  console.log('=== Signing via Server ===');

  try {
    // Step 1: Check if Java Sign Server is running
    console.log('Step 1: Checking  Sign Server...');
    const JAVA_SERVER_URL = 'http://localhost:14500';

    try {
      const healthCheck = await fetch(`${JAVA_SERVER_URL}/health`);
      if (!healthCheck.ok) {
        throw new Error(' Sign Server không phản hồi');
      }
      const healthData = await healthCheck.json();
      console.log('✅ Server đang chạy, Certificate:', healthData.certificate);
    } catch (err) {
      throw new Error(
        'SignServerTools chưa chạy!'
      );
    }

    // Step 2: Call Java Sign Server API
    console.log('Step 2: Gọi  API để ký XML...');
    console.log('XML length:', xmlContent.length);

    const response = await fetch(`${JAVA_SERVER_URL}/sign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: xmlContent,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMsg = errorData.error || 'Ký số thất bại';

      // 503 = USB Token / Certificate error (show detailed error)
      // 500 = Server error (show generic error)
      if (response.status === 503) {
        throw new Error(errorMsg); // USB Token error with detailed instructions
      }

      throw new Error('Lỗi server khi ký XML: ' + errorMsg);
    }

    const data = await response.json();

    if (!data.success || !data.signedXml) {
      throw new Error(data.error || 'Không nhận được XML đã ký');
    }

    // Step 3: Decode base64 response
    const signedXml = atob(data.signedXml);

    return signedXml;

  } catch (error: any) {
    console.error('=== BHYT XML Signing Failed ===');
    console.error('Error:', error);

    // Network/Connection errors
    if (error.message?.includes('fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Failed to fetch')) {
      throw new Error(
        'Không thể kết nối đến SignServerTools!\n\n' +
        'Vui lòng kiểm tra:\n' +
        '1. Server đã chạy chưa? (Xem icon ở system tray)\n' +
        '2. Port 14500 có bị chặn không?\n' +
        '3. Thử chạy lại: SignServerTools.bat'
      );
    }

    // Otherwise re-throw the error (already formatted by status code check above)
    throw error;
  }
}

// ========================================
// IUsbTokenProvider Implementation
// ========================================
class ViettelCAProvider implements IUsbTokenProvider {
  initPlugin(): void {
    initPlugin();
  }

  setDomain(newDomain: string): void {
    if (!newDomain.endsWith("/")) newDomain += "/";
    domain = newDomain;
  }

  getDomain(): string {
    return domain;
  }

  async signFile(
    file: Blob | File,
    fileName: string,
    callback: (result: { file1: string; file2?: string }) => void
  ): Promise<void> {
    // For ViettelCA, we use the plugin-based file signing flow
    // This is a wrapper around the internal file signing functions
    return Promise.reject(new Error('signFile not yet implemented for ViettelCAProvider - use browseFile + internalSignFile for now'));
  }

  async signXMLData(xmlContent: string): Promise<string> {
    // Default: Use Java Sign Server for BHYT XML signing
    return signBHYTXml(xmlContent);
  }
}

// ========================================
// Export Default Instance
// ========================================
const viettelCAProvider = new ViettelCAProvider();

export default {
  // IUsbTokenProvider interface
  initPlugin: viettelCAProvider.initPlugin.bind(viettelCAProvider),
  setDomain: viettelCAProvider.setDomain.bind(viettelCAProvider),
  getDomain: viettelCAProvider.getDomain.bind(viettelCAProvider),
  signXMLData: viettelCAProvider.signXMLData.bind(viettelCAProvider),

  // Additional ViettelCA-specific functions
  getCertificate,
  signData,
  browseFile,
  saveFile,
  signFile: internalSignFile,  // Internal file signing via ViettelCA Plugin

  // XML Signing Options
  signXMLDataViaPlugin,  // Via ViettelCA Plugin (Port 14007)
  signBHYTXml,           // Via Java Server (Port 14500) - RECOMMENDED for BHYT

  signFileFromUI: async (..._args: any[]) => {
    throw new Error('signFileFromUI has been removed - please use separate PDF signing service');
  },
  signFileFromUIBlob: async (..._args: any[]) => {
    throw new Error('signFileFromUIBlob has been removed - please use separate PDF signing service');
  },
  signFileWithFileSignAPI: async (..._args: any[]) => {
    throw new Error('signFileWithFileSignAPI has been removed - please use separate PDF signing service');
  },
};
